import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, Trash2, Eye, Smartphone, Tablet, Monitor } from 'lucide-react'
import type { Tier1VisualConfig as Tier1VisualConfigType } from '@/types'

interface Tier1VisualConfigProps {
  config: Tier1VisualConfigType
  onChange: (config: Tier1VisualConfigType) => void
  onLaunchEditor?: () => void
}

export function Tier1VisualConfig({ config, onChange, onLaunchEditor }: Tier1VisualConfigProps) {
  const [expandedMod, setExpandedMod] = useState<number | null>(null)

  const modifications = config.modifications || []
  const elementsToHide = config.elements_to_hide || []
  const capturedEvents = config.captured_events || []

  const getDeviceIcon = (device?: string) => {
    switch (device) {
      case 'mobile':
        return <Smartphone className="h-3 w-3" />
      case 'tablet':
        return <Tablet className="h-3 w-3" />
      case 'desktop':
        return <Monitor className="h-3 w-3" />
      default:
        return null
    }
  }

  const handleRemoveModification = (index: number) => {
    const updated = [...modifications]
    updated.splice(index, 1)
    onChange({ ...config, modifications: updated })
  }

  const handleRemoveElement = (index: number) => {
    const updated = [...elementsToHide]
    updated.splice(index, 1)
    onChange({ ...config, elements_to_hide: updated })
  }

  const handleUpdateModification = (index: number, field: string, value: any) => {
    const updated = [...modifications]
    updated[index] = { ...updated[index], [field]: value }
    onChange({ ...config, modifications: updated })
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{modifications.length}</div>
            <p className="text-xs text-muted-foreground">Modificaciones de Estilo</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{elementsToHide.length}</div>
            <p className="text-xs text-muted-foreground">Elementos Ocultos</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{capturedEvents.length}</div>
            <p className="text-xs text-muted-foreground">Eventos Capturados</p>
          </CardContent>
        </Card>
      </div>

      {/* Visual Modifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Modificaciones de Estilo</CardTitle>
              <CardDescription>
                Cambios visuales aplicados a elementos específicos
              </CardDescription>
            </div>
            {onLaunchEditor && (
              <Button onClick={onLaunchEditor} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Agregar con Editor
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {modifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No hay modificaciones de estilo aún</p>
              <p className="text-xs mt-1">Usa el Editor Visual para crear modificaciones</p>
            </div>
          ) : (
            <div className="space-y-3">
              {modifications.map((mod, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                          {mod.selector}
                        </code>
                        {mod.device && mod.device !== 'all' && (
                          <Badge variant="outline" className="text-xs">
                            {getDeviceIcon(mod.device)}
                            <span className="ml-1">{mod.device}</span>
                          </Badge>
                        )}
                      </div>
                      {mod.urlPattern && mod.urlPattern !== '/*' && (
                        <div className="text-xs text-muted-foreground">
                          Página: <code className="bg-muted px-1 rounded">{mod.urlPattern}</code>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedMod(expandedMod === index ? null : index)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveModification(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>

                  {expandedMod === index && (
                    <div className="space-y-3 pt-3 border-t">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Patrón de URL</Label>
                          <Input
                            value={mod.urlPattern || '/*'}
                            onChange={(e) => handleUpdateModification(index, 'urlPattern', e.target.value)}
                            placeholder="/*"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Dispositivo</Label>
                          <Select
                            value={mod.device || 'all'}
                            onValueChange={(value) => handleUpdateModification(index, 'device', value)}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos</SelectItem>
                              <SelectItem value="desktop">Desktop</SelectItem>
                              <SelectItem value="mobile">Mobile</SelectItem>
                              <SelectItem value="tablet">Tablet</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Cambios Aplicados</Label>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          {Object.entries(mod.changes || {}).map(([key, value]) => (
                            <div key={key} className="text-xs bg-muted px-2 py-1 rounded">
                              <span className="font-medium">{key}:</span> {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Elements to Hide */}
      <Card>
        <CardHeader>
          <CardTitle>Elementos Ocultos</CardTitle>
          <CardDescription>
            Elementos que se ocultan en la variante de tratamiento
          </CardDescription>
        </CardHeader>
        <CardContent>
          {elementsToHide.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No hay elementos ocultos aún</p>
            </div>
          ) : (
            <div className="space-y-3">
              {elementsToHide.map((elem, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                          {elem.selector}
                        </code>
                        <Badge variant={elem.hideIn === 'treatment' ? 'default' : 'secondary'} className="text-xs">
                          Oculto en: {elem.hideIn === 'treatment' ? 'Variante' : 'Control'}
                        </Badge>
                        {elem.device && elem.device !== 'all' && (
                          <Badge variant="outline" className="text-xs">
                            {getDeviceIcon(elem.device)}
                            <span className="ml-1">{elem.device}</span>
                          </Badge>
                        )}
                      </div>
                      {elem.urlPattern && elem.urlPattern !== '/*' && (
                        <div className="text-xs text-muted-foreground">
                          Página: <code className="bg-muted px-1 rounded">{elem.urlPattern}</code>
                        </div>
                      )}
                      {elem.description && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {elem.description}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveElement(index)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Captured Events */}
      {capturedEvents.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Eventos Capturados</CardTitle>
            <CardDescription>
              Eventos personalizados capturados desde el Editor Visual
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {capturedEvents.map((event, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium">{event.displayName}</div>
                    <Badge variant="outline" className="text-xs">
                      {event.suggestedType}
                    </Badge>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div>
                      <span className="font-medium">Selector:</span>{' '}
                      <code className="bg-muted px-1 rounded">{event.selector.selector}</code>
                    </div>
                    <div>
                      <span className="font-medium">Elemento:</span>{' '}
                      {event.elementContext.tagName}
                      {event.elementContext.textContent && ` - "${event.elementContext.textContent.substring(0, 50)}"`}
                    </div>
                    <div>
                      <span className="font-medium">Confianza:</span>{' '}
                      {(event.selector.confidence * 100).toFixed(0)}% ({event.selector.matchCount} elementos)
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm">
              <p className="text-blue-900">
                💡 Estos eventos fueron capturados desde el Editor Visual. Ve a{' '}
                <strong>Eventos Personalizados</strong> para activarlos y empezar a rastrearlos.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
